import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import { useTheme } from '../../hooks/useTheme.jsx'
import { formatDate } from '../../utils/helpers'

/**
 * Every project's archive and retention position, on one page. Admin only.
 *
 * Read-only on purpose. Purging and legal holds happen on the project itself, where the files and
 * the reason are in front of the person doing it; this page answers "what do we hold, and when may
 * it go?" and takes you there. The figures are the same ones the project's own retention card
 * shows, produced by the same code, so the two cannot disagree.
 */

const STATE = {
  held: { label: 'On legal hold', cls: 'bg-purple-100 text-purple-800', hint: 'Nothing about it can be deleted until the hold is lifted.' },
  expired: { label: 'Past retention', cls: 'bg-red-100 text-red-800', hint: 'Files can be purged now, after an Admin confirms.' },
  ending_soon: { label: 'Ending soon', cls: 'bg-amber-100 text-amber-900', hint: 'Retention ends within 90 days.' },
  kept: { label: 'Kept', cls: 'bg-green-100 text-green-800', hint: 'Within its retention period.' },
}

const PERSON = {
  eligible: { label: 'Ready to erase', cls: 'bg-red-100 text-red-800' },
  held: { label: 'On legal hold', cls: 'bg-purple-100 text-purple-800' },
  waiting: { label: 'Waiting for the year', cls: 'bg-amber-100 text-amber-900' },
  done: { label: 'Erased', cls: 'bg-green-100 text-green-800' },
}

const FILTERS = [['all', 'All'], ['held', 'On hold'], ['expired', 'Past retention'], ['ending_soon', 'Ending in 90 days'], ['kept', 'Kept']]

export default function ArchiveOverview() {
  const { isDark } = useTheme()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [people, setPeople] = useState(null)
  const [note, setNote] = useState(null)
  const [busy, setBusy] = useState(null)

  const loadPeople = () => api.get('/personal-files/').then(r => setPeople(r.data)).catch(() => setPeople(null))

  useEffect(() => {
    api.get('/closure/overview')
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.status === 403
        ? 'Only an Admin can open this page.'
        : err.response?.data?.detail || 'Could not load the archive overview.'))
    loadPeople()
  }, [])

  // Erasing a person's files is the one action on this page, so it asks twice: once in plain words, once
  // for who asked when it is a request. The server checks the rules again and says why when it refuses.
  const erase = async (p, reason) => {
    let why = ''
    if (reason === 'request') {
      why = window.prompt(`Erase ${p.name}'s files at their request.\n\nWho asked, and how? (This is written to the record.)`) || ''
      if (why.trim().length < 5) return
    } else if (!window.confirm(`Erase the CV, identity documents, bank details and consents of ${p.name}?\n\nTheir account and name stay so past records still say who did the work. This cannot be undone.`)) return
    setBusy(p.user_id); setNote(null)
    try {
      const r = await api.post(`/personal-files/${p.user_id}/erase`, { reason, note: why })
      setNote({ ok: true, text: r.data?.detail })
      await loadPeople()
    } catch (e) {
      setNote({ ok: false, text: e.response?.data?.detail || 'Could not erase.' })
    } finally { setBusy(null) }
  }

  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  const primary = isDark ? 'text-white' : 'text-gray-900'
  const secondary = isDark ? 'text-gray-400' : 'text-gray-500'

  const rows = (data?.projects || []).filter(p => filter === 'all' || p.state === filter)
  const s = data?.summary || {}

  const files = (b) => (b.archived ? `${b.archived} archived${b.purged ? ` · ${b.purged} purged` : ''}` : '—')

  return (
    <div className={`min-h-full p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <h1 className={`text-2xl font-bold ${primary}`}>Archive &amp; retention</h1>
          <p className={`text-sm mt-1 ${secondary}`}>
            Every archived project, what is on a legal hold, and when its files may go.
            Purging files and placing a hold happen on the project itself. The one action on this page is
            erasing a former consultant&apos;s personal files, and it never happens without you.
          </p>
        </div>

        {error && <div className={`rounded-xl border p-6 ${card}`}><p className="text-sm text-red-600">{error}</p></div>}
        {!data && !error && <p className={`text-sm ${secondary}`}>Reading every project…</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['held', 'expired', 'ending_soon', 'kept'].map(k => (
                <button
                  key={k}
                  onClick={() => setFilter(filter === k ? 'all' : k)}
                  className={`text-left rounded-xl border p-4 transition ${card} ${filter === k ? 'ring-2 ring-indigo-500' : ''}`}
                >
                  <p className={`text-2xl font-bold ${primary}`}>{s[k] || 0}</p>
                  <p className={`text-xs mt-1 ${secondary}`}>{STATE[k].label}</p>
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {FILTERS.map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={`px-3 py-1 text-xs rounded-full border ${filter === k
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : (isDark ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600 hover:bg-gray-100')}`}
                >{label}</button>
              ))}
            </div>

            {rows.length === 0 ? (
              <div className={`rounded-xl border p-8 text-center ${card}`}>
                <p className={`text-sm ${secondary}`}>
                  {data.projects.length === 0
                    ? 'Nothing has been archived yet.'
                    : 'No project matches this filter.'}
                </p>
              </div>
            ) : (
              <div className={`rounded-xl border overflow-x-auto ${card}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`text-left text-xs ${secondary}`}>
                      <th className="p-3">Project</th>
                      <th className="p-3">Position</th>
                      <th className="p-3">Archived</th>
                      <th className="p-3">Deliverables ({data.deliverable_retention_years} yr)</th>
                      <th className="p-3">Documents ({data.document_retention_years} yr)</th>
                      <th className="p-3">Money records ({data.financial_retention_years} yr)</th>
                      <th className="p-3">Next retention end</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(p => (
                      <tr key={p.project_id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className="p-3">
                          <Link to={`/projects/${p.project_id}`} className="font-medium text-indigo-600 hover:underline">{p.name}</Link>
                          {p.project_number && <span className={`ml-2 text-xs ${secondary}`}>{p.project_number}</span>}
                          {p.legal_hold && (
                            <p className={`text-xs mt-0.5 ${secondary}`}>Reason: {p.legal_hold_reason || 'none recorded'}</p>
                          )}
                        </td>
                        <td className="p-3">
                          <span title={STATE[p.state].hint} className={`text-xs px-2 py-0.5 rounded-full ${STATE[p.state].cls}`}>
                            {STATE[p.state].label}
                          </span>
                          {p.expired > 0 && p.state !== 'expired' && (
                            <p className={`text-xs mt-0.5 ${secondary}`}>{p.expired} past retention</p>
                          )}
                        </td>
                        <td className={`p-3 ${secondary}`}>{p.archived_at ? formatDate(p.archived_at) : 'Not archived'}</td>
                        <td className={`p-3 ${secondary}`}>{files(p.deliverables)}</td>
                        <td className={`p-3 ${secondary}`}>{files(p.documents)}</td>
                        <td className={`p-3 ${secondary}`}>{files(p.financial)}</td>
                        <td className={`p-3 ${primary}`}>{p.next_expiry_date ? formatDate(p.next_expiry_date) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Closed and never archived: no retention clock is running on these, so nothing about them will
                ever expire. Listed so they are not forgotten; the archive button is on the project. */}
            {data.awaiting_archive?.length > 0 && (
              <div className="space-y-2">
                <h2 className={`text-lg font-semibold ${primary}`}>Waiting to be archived</h2>
                <p className={`text-xs ${secondary}`}>
                  Closed but not archived. Until a project is archived it is not read-only and no retention period
                  is running. You are reminded weekly once one has waited {data.archive_after_close_days} days.
                </p>
                <div className={`rounded-xl border overflow-x-auto ${card}`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`text-left text-xs ${secondary}`}>
                        <th className="p-3">Project</th>
                        <th className="p-3">Closed</th>
                        <th className="p-3">Waiting</th>
                        <th className="p-3">Terms of reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.awaiting_archive.map(w => (
                        <tr key={w.project_id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                          <td className="p-3">
                            <Link to={`/projects/${w.project_id}/closure`} className="font-medium text-indigo-600 hover:underline">{w.name}</Link>
                            {w.project_number && <span className={`ml-2 text-xs ${secondary}`}>{w.project_number}</span>}
                          </td>
                          <td className={`p-3 ${secondary}`}>{w.closed_at ? formatDate(w.closed_at) : '—'}</td>
                          <td className={`p-3 ${w.overdue ? 'text-amber-600 font-medium' : secondary}`}>
                            {w.days_closed != null ? `${w.days_closed} day${w.days_closed === 1 ? '' : 's'}` : '—'}{w.overdue ? ' · overdue' : ''}
                          </td>
                          <td className={`p-3 ${w.has_tor ? secondary : 'text-red-600'}`}>{w.has_tor ? 'On file' : 'Missing — needed to archive'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* People who have left. Their CV, identity documents, bank details and consents are kept one year
                after the engagement ends and then erased by an Admin, one person at a time. */}
            {people && people.people.length > 0 && (
              <div className="space-y-2">
                <h2 className={`text-lg font-semibold ${primary}`}>People&apos;s files</h2>
                <p className={`text-xs ${secondary}`}>
                  A consultant&apos;s CV, identity documents, bank details and consents are kept {people.years} year
                  after their engagement ends. Nothing is erased until you do it here. A legal hold on any of their
                  projects stops it. Their account and name stay.
                </p>
                {note && (
                  <p className={`text-sm rounded-lg border px-3 py-2 ${note.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>{note.text}</p>
                )}
                <div className={`rounded-xl border overflow-x-auto ${card}`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`text-left text-xs ${secondary}`}>
                        <th className="p-3">Person</th>
                        <th className="p-3">Position</th>
                        <th className="p-3">Engagement ended</th>
                        <th className="p-3">Ready to erase</th>
                        <th className="p-3">Files</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {people.people.map(p => (
                        <tr key={p.user_id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                          <td className={`p-3 ${primary}`}>{p.name}<span className={`block text-xs ${secondary}`}>{p.email}</span></td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${PERSON[p.state].cls}`}>{PERSON[p.state].label}</span>
                            {p.state === 'held' && <p className={`text-xs mt-0.5 ${secondary}`}>{p.held_by.join(', ')}</p>}
                          </td>
                          <td className={`p-3 ${secondary}`}>{p.ended_on ? formatDate(p.ended_on) : '—'}</td>
                          <td className={`p-3 ${primary}`}>{p.eligible_on ? formatDate(p.eligible_on) : '—'}</td>
                          <td className={`p-3 ${secondary}`}>{p.files}{p.has_details ? ' + details' : ''}</td>
                          <td className="p-3 text-right whitespace-nowrap">
                            {p.state === 'eligible' && (
                              <button disabled={busy === p.user_id} onClick={() => erase(p, 'retention')}
                                className="px-3 py-1 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">Erase files</button>
                            )}
                            {p.state === 'waiting' && (
                              <button disabled={busy === p.user_id} onClick={() => erase(p, 'request')}
                                title="Only if the person has asked to be forgotten"
                                className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40">Erase on request</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Files put away in a product room carry the same clock. They belong to the platform, not to
                a project, so they get their own short list. */}
            {data.product_rooms?.length > 0 && (
              <div className="space-y-2">
                <h2 className={`text-lg font-semibold ${primary}`}>Product rooms</h2>
                <p className={`text-xs ${secondary}`}>
                  Files put away in a product room are kept {data.product_room_years} years from the day they were put away.
                  Nothing is deleted when that ends.
                </p>
                <div className={`rounded-xl border overflow-x-auto ${card}`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`text-left text-xs ${secondary}`}>
                        <th className="p-3">Room</th>
                        <th className="p-3">Put away</th>
                        <th className="p-3">Past retention</th>
                        <th className="p-3">Next retention end</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.product_rooms.map(r => (
                        <tr key={`${r.product_key}-${r.country}`} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                          <td className="p-3">
                            <Link to={`/products/${r.product_key}/rooms`} className="font-medium text-indigo-600 hover:underline">
                              {r.product_name} · {r.country}
                            </Link>
                          </td>
                          <td className={`p-3 ${secondary}`}>{r.archived}</td>
                          <td className={`p-3 ${r.expired ? 'text-red-600 font-medium' : secondary}`}>{r.expired}</td>
                          <td className={`p-3 ${primary}`}>{r.next_expiry_date ? formatDate(r.next_expiry_date) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
